import { Input, PasswordInput } from "../../Components/ui/input";
import React, { useEffect, useState } from "react";
import { Button } from "./../../Components/ui/button";
import { userLogin, getRecaptchaToken, loadRecaptcha } from "./../../api";
import { useNavigate, useSearchParams } from "react-router-dom";
import ButtonLoader from "./../../Components/Loader/index";
import SignUpFlow from "./Layout";
import { useAuth } from "./../../hooks";
import TwoFactor from "./TwoFactor";

function LoginAccount() {
  const { userName, setStateVariable } = useAuth();
  const [usernameForm, setUsernameForm] = useState(userName || "");
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [PassError, setPassError] = useState("");
  const [loading, setLoading] = useState(false);
  // Login response when 2FA is needed; shows the 2FA flow in place of the login form
  const [twoFactorContext, setTwoFactorContext] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") || "";

  useEffect(() => {
    if (!userName) {
      navigate(`/`);
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialise reCAPTCHA v3 on page load so the token is quick to get on submit
  useEffect(() => {
    loadRecaptcha();
  }, []);

  // Routes the user once a session exists (after a plain login or after 2FA)
  const redirectAfterLogin = (resp) => {
    if (
      resp?.data?.user?.status === "APPROVED" &&
      resp?.data?.user?.currentStep === "ACTIVE" &&
      resp?.data?.accountStatus === "APPROVED"
    ) {
      navigate("/home", {
        replace: true,
      });
      return;
    } else if (resp?.data?.user?.currentStep === "REGISTER") {
      setStateVariable(1);
      navigate("/details", {
        replace: true,
      });
      return;
    } else if (resp?.data?.user?.currentStep === "PERSONAL_INFO") {
      setStateVariable(2);
      navigate("/details", {
        replace: true,
      });
      return;
    } else if (resp?.data?.user?.currentStep === "SIGNUP") {
      setStateVariable(3);
      navigate("/details", {
        replace: true,
      });
      return;
    } else if (resp?.data?.user?.currentStep === "DOC_UPLOAD") {
      setStateVariable(5);
      navigate("/details", {
        replace: true,
      });
      return;
    } else if (resp?.data?.user?.currentStep === "SELFIEE_UPLOAD") {
      navigate("/redirect?status=success", {
        replace: true,
      });
      return;
    } else {
      // if (
      //   resp?.data?.user?.currentStep === "SELFIEE" ||
      //   resp?.data?.user?.status !== "APPROVED"
      // ) {
      navigate("/homeVerification", {
        replace: true,
      });
      // }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!usernameForm) {
      setError("username  is required.");
      setLoading(false);
      return;
    }
    if (!password) {
      setPassError("Password  is required.");
      setLoading(false);
      return;
    }
    try {
      // Fresh token per attempt (tokens are single-use, ~2 min expiry)
      const recaptchaToken = await getRecaptchaToken("login");
      if (!recaptchaToken) {
        setPassError(
          "Verification could not be completed. Please refresh the page and try again.",
        );
        setLoading(false);
        return;
      }

      const data = {
        username: usernameForm,
        password: password,
        source: "web",
        recaptchaToken,
      };
      sessionStorage.clear();
      const resp = await userLogin(data);

      if (resp) {
        // The challenge fields may arrive flat or under `data`
        const challenge =
          resp?.data?.twoFactorRequired !== undefined ? resp.data : resp;
        if (challenge?.twoFactorRequired === true) {
          setTwoFactorContext(challenge);
        } else {
          redirectAfterLogin(resp);
        }
        setLoading(false);
      }
    } catch (error) {
      setPassError(error?.message);
      setError(" ");
      setLoading(false);
      return;
    }

    setError("");
    setPassError("");
    setLoading(false);
    // setStateVariable(stateVariable + 1);
  };

  if (twoFactorContext) {
    return (
      <SignUpFlow>
        <TwoFactor
          context={twoFactorContext}
          onSuccess={redirectAfterLogin}
          onBackToLogin={() => {
            setTwoFactorContext(null);
            setPassword("");
          }}
        />
      </SignUpFlow>
    );
  }

  return (
    <SignUpFlow
      headerText={"Welcome Back"}
      ParagraphText={"Enter your unique username and password to login"}
    >
      <form onSubmit={handleSubmit}>
        <div className="space-y-[22px]">
          <div>
            <label
              htmlFor="username"
              className="block text-[16px] font-geomanistRegular text-greyish mb-[9px] ml-2"
            >
              Username
            </label>
            <Input
              id="username"
              type="text"
              value={usernameForm}
              disabled
              onChange={(e) => setUsernameForm(e.target.value)}
              className={`w-full h-[45px] px-3 py-2 font-geomanistBold text-[16px] ${
                error ? "border-red-500" : ""
              }`}
            />
            {error && (
              <p className="text-red-500  text-[14px] mt-1 ml-2">{error}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="password"
              className="block text-[16px] font-geomanistRegular text-greyish mb-[9px] ml-2"
            >
              Password
            </label>

            <PasswordInput
              id="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError("");
                setPassError("");
              }}
              error={PassError}
              className="font-geomanistBold text-[16px]"
            />
            {PassError && (
              <p className="text-red-500  text-[14px] mt-1 ml-2">{PassError}</p>
            )}
          </div>
        </div>

        <div className="flex justify-between text-sm mt-[14px] mb-[19px]">
          <div
            className="text-blueish font-geomanistRegular font-[14px] hover:text-blue-500 cursor-pointer hover:cursor-pointer"
            onClick={() => {
              navigate(
                `/forgot?email=${encodeURIComponent(
                  email,
                )}&username=${encodeURIComponent(usernameForm)}`,
              );
              // setCurrentFlow("forgotPassword");
              // setStateVariable(1);
            }}
          >
            Forgot Password?
          </div>

          {/* <div
              className="text-blueish hover:text-blue-500  font-geomanistRegular font-[14px]"
              onClick={() => {
                navigate("/exist");
              }}
            >
              Forgot Username?
            </div> */}
        </div>

        <Button type="submit" variant="primary" disabled={loading}>
          {loading && <ButtonLoader className={"mr-2"} />} Login
        </Button>
      </form>

      <p className="text-center mt-[42px] text-[18px] text-blackShade font-geomanistRegular">
        Don't have an account?
        <span
          className="text-blueish hover:text-blue-500  font-[500]   font-geomanistMedium cursor-pointer px-1"
          onClick={() => {
            navigate(`/email?email=${encodeURIComponent(email)}`);
          }}
        >
          Sign up
        </span>
        here
      </p>
    </SignUpFlow>
  );
}

export default LoginAccount;
